import { Box3, BufferAttribute, Float32BufferAttribute, Matrix4, Triangle, Vector3 } from 'three';
import type { MeshStandardMaterial } from 'three';
import type JawsCorrection from './JawsCorrection';
import type ToothCorrection from './ToothCorrection';

/** Distances beyond this render as the base tooth color. */
const MAX_DIST_MM = 2;

/**
 * Per-vertex heat map of the occlusal clearance between the two jaws: every
 * tooth's vertices are colored by their distance to the OPPOSING jaw's teeth.
 * Penetration is dark red, contact ramps red → yellow → green and fades to
 * the plain tooth color at {@link MAX_DIST_MM}.
 *
 * Query points come from the RENDER BUFFER through `matrixWorld`, never from
 * the half-edge `vertex.position`: centerOrigin() seeds those with the
 * centered LOCAL coords and only a later rotateHalfEdges() rewrites them as
 * world, so on a freshly loaded scan they are not world coordinates at all.
 * Distances then come from the opposing teeth's BVHs; tooth transforms are
 * rigid, so local distances equal world distances.
 *
 * Refreshes on transform end while enabled; treatment-cycle playback leaves
 * the map stale (acceptable for v1 — toggling it re-syncs).
 */
export default class OcclusionContactMap {
  private readonly jaws: JawsCorrection;
  private enabled = false;
  /** Teeth whose material currently has vertexColors switched on. */
  private readonly coloredTeeth = new Set<ToothCorrection>();

  constructor(jaws: JawsCorrection) {
    this.jaws = jaws;
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  public setEnabled(enabled: boolean) {
    if (this.enabled === enabled) return;
    this.enabled = enabled;
    if (enabled) {
      this.refresh();
    } else {
      this.clearColors();
    }
  }

  public refreshIfEnabled() {
    if (this.enabled) this.refresh();
  }

  public dispose() {
    this.clearColors();
  }

  private refresh() {
    this.colorJaw(this.jaws.upperJaw.teeth, this.jaws.lowerJaw.teeth);
    this.colorJaw(this.jaws.lowerJaw.teeth, this.jaws.upperJaw.teeth);
  }

  private colorJaw(teeth: ToothCorrection[], opposing: ToothCorrection[]) {
    // setPosition() does not flush the matrix, and a refresh can land in the
    // same tick as a programmatic transform — resync before reading matrixWorld.
    for (const tooth of [...teeth, ...opposing]) {
      tooth.updateWorldMatrix(true, false);
    }

    const opposingBoxes = opposing.map((tooth) => {
      tooth.geometry.computeBoundingBox();
      return new Box3()
        .copy(tooth.geometry.boundingBox!)
        .applyMatrix4(tooth.matrixWorld)
        .expandByScalar(MAX_DIST_MM);
    });

    const toothBox = new Box3();
    for (const tooth of teeth) {
      tooth.geometry.computeBoundingBox();
      toothBox.copy(tooth.geometry.boundingBox!).applyMatrix4(tooth.matrixWorld);
      const candidates = opposing.filter((_, i) => opposingBoxes[i].intersectsBox(toothBox));
      this.colorTooth(tooth, candidates);
    }
  }

  private colorTooth(tooth: ToothCorrection, candidates: ToothCorrection[]) {
    const count = tooth.geometry.attributes.position.count;
    let colorAttr = tooth.geometry.getAttribute('color') as BufferAttribute | undefined;
    if (!colorAttr || colorAttr.count !== count) {
      colorAttr = new Float32BufferAttribute(new Float32Array(count * 3), 3);
      tooth.geometry.setAttribute('color', colorAttr);
    }
    const colors = colorAttr.array as Float32Array;
    colors.fill(1); // base = white → material color shows through unchanged

    if (candidates.length > 0) {
      const inverses = candidates.map((c) => new Matrix4().copy(c.matrixWorld).invert());
      const position = tooth.geometry.attributes.position;
      const world = new Vector3();
      const local = new Vector3();
      const closestWorld = new Vector3();
      const offset = new Vector3();
      const triangle = new Triangle();
      const normal = new Vector3();
      const hit = { point: new Vector3(), distance: 0, faceIndex: 0 };

      for (let renderId = 0; renderId < count; renderId++) {
        world.fromBufferAttribute(position, renderId).applyMatrix4(tooth.matrixWorld);
        let best = Infinity;
        let penetrating = false;

        for (let i = 0; i < candidates.length; i++) {
          const candidate = candidates[i];
          const bvh = candidate.geometry.boundsTree;
          if (!bvh) continue;
          local.copy(world).applyMatrix4(inverses[i]);
          const result = bvh.closestPointToPoint(local, hit, 0, MAX_DIST_MM);
          // three-mesh-bvh can hand back a hit beyond maxThreshold — always
          // re-check the distance instead of trusting the bound.
          if (!result || hit.distance >= best || hit.distance > MAX_DIST_MM) continue;
          best = hit.distance;
          this.faceTriangle(candidate, hit.faceIndex, triangle);
          triangle.getNormal(normal).applyQuaternion(candidate.quaternion);
          closestWorld.copy(hit.point).applyMatrix4(candidate.matrixWorld);
          penetrating = normal.dot(offset.copy(world).sub(closestWorld)) < 0;
        }

        if (best <= MAX_DIST_MM) {
          this.writeRampColor(colors, renderId, penetrating ? -best : best);
        }
      }
    }

    colorAttr.needsUpdate = true;
    const material = tooth.material as MeshStandardMaterial;
    if (!material.vertexColors) {
      material.vertexColors = true;
      material.needsUpdate = true;
    }
    this.coloredTeeth.add(tooth);
  }

  /** Local-space triangle of `faceIndex`, in the CURRENT index order the BVH reported it in. */
  private faceTriangle(tooth: ToothCorrection, faceIndex: number, out: Triangle) {
    const index = tooth.geometry.getIndex();
    const position = tooth.geometry.attributes.position;
    const base = faceIndex * 3;
    const at = (offset: number) => (index ? index.getX(base + offset) : base + offset);
    out.a.fromBufferAttribute(position, at(0));
    out.b.fromBufferAttribute(position, at(1));
    out.c.fromBufferAttribute(position, at(2));
  }

  /** Signed distance → RGB: <0 dark red, 0→2 mm red → yellow → green → white. */
  private writeRampColor(colors: Float32Array, renderId: number, signedDistance: number) {
    let r: number;
    let g: number;
    let b: number;
    if (signedDistance < 0) {
      r = 0.55;
      g = 0;
      b = 0;
    } else {
      const t = signedDistance / MAX_DIST_MM; // 0..1
      if (t < 1 / 3) {
        const k = t * 3; // red → yellow
        r = 1;
        g = k;
        b = 0;
      } else if (t < 2 / 3) {
        const k = (t - 1 / 3) * 3; // yellow → green
        r = 1 - k;
        g = 1;
        b = 0;
      } else {
        const k = (t - 2 / 3) * 3; // green → white
        r = k;
        g = 1;
        b = k;
      }
    }
    const idx = renderId * 3;
    if (idx + 2 >= colors.length) return;
    colors[idx] = r;
    colors[idx + 1] = g;
    colors[idx + 2] = b;
  }

  private clearColors() {
    for (const tooth of this.coloredTeeth) {
      const material = tooth.material as MeshStandardMaterial;
      material.vertexColors = false;
      material.needsUpdate = true;
    }
    this.coloredTeeth.clear();
  }
}
